import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Icon,
  Label,
  Message,
  Breadcrumb,
  Table,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MARKDOWN_BODY_CSS } from '../docs/markdownBodyCss';
import { getApiPath, getAuthHeaders } from '../../api';
import markdownComponents from '../markdownComponents';

export default function PolicyView() {
  const { docId } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchPolicy() {
      try {
        const headers = getAuthHeaders();
        const res = await fetch(
          `${getApiPath()}/@polyglot/policies/${docId}`,
          {
            headers: Object.keys(headers).length > 0 ? headers : undefined,
          },
        );
        if (res.status === 404) {
          setError('Policy not found or you do not have access.');
          setLoading(false);
          return;
        }
        if (!res.ok) throw new Error('Failed to load policy');
        const data = await res.json();
        setPolicy(data);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchPolicy();
  }, [docId]);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '300px' }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Policy Not Found" />
        <Message error icon>
          <Icon name="exclamation triangle" />
          <Message.Content>
            <Message.Header>Policy Not Available</Message.Header>
            {error}
          </Message.Content>
        </Message>
        <Link to="/policies">
          <Icon name="arrow left" /> Back to Policies
        </Link>
      </Container>
    );
  }

  if (!policy) return null;

  // Prepare markdown body: strip frontmatter and first H1 (shown as page header)
  const prepareMarkdown = (md) => {
    if (!md) return '';
    let text = md.replace(/^---[\s\S]*?---\s*/, '');
    text = text.replace(/^#\s+.*\n+/, '');
    return text;
  };

  return (
    <Container style={{ paddingTop: '2em', maxWidth: '800px' }}>
      <Helmet title={policy.title} />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/policies">
          Policies
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section active>{policy.doc_id}</Breadcrumb.Section>
      </Breadcrumb>

      <Segment>
        <Header as="h1">{policy.title}</Header>
        <div style={{ marginBottom: '1.5em' }}>
          <Label size="small">{policy.doc_id}</Label>
          {policy.version && (
            <Label size="small" basic>
              Version {policy.version}
            </Label>
          )}
          {policy.effective_date && (
            <Label size="small" basic>
              <Icon name="calendar" /> Effective: {policy.effective_date}
            </Label>
          )}
          {policy.last_updated && (
            <Label size="small" basic>
              <Icon name="clock" /> Updated: {new Date(policy.last_updated).toLocaleDateString()}
            </Label>
          )}
        </div>
        {policy.owner && (
          <p style={{ color: '#666', fontSize: '0.9em', marginBottom: '1.5em' }}>
            <Icon name="user" /> Policy owner: <strong>{policy.owner}</strong>
            {policy.review_interval && (
              <span> · Review cycle: {policy.review_interval}</span>
            )}
          </p>
        )}

        <style>{MARKDOWN_BODY_CSS}</style>
        <div className="policy-body md-body" style={{ lineHeight: '1.7', fontSize: '1.05em' }}>
          <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {prepareMarkdown(policy.body_md)}
          </Markdown>
        </div>
      </Segment>

      {policy.history && policy.history.length > 0 && (
        <Segment>
          <Header as="h3">
            <Icon name="history" />
            <Header.Content>Changelog</Header.Content>
          </Header>
          <Table basic="very" compact>
            <Table.Body>
              {policy.history.map((h) => (
                <Table.Row key={h.version} verticalAlign="top">
                  <Table.Cell collapsing>
                    <Label size="tiny" basic>
                      v{h.version}
                    </Label>
                  </Table.Cell>
                  <Table.Cell
                    collapsing
                    style={{ color: '#999', whiteSpace: 'nowrap' }}
                  >
                    {h.dated ? new Date(h.dated).toLocaleDateString() : ''}
                  </Table.Cell>
                  <Table.Cell>{h.changes || '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
    </Container>
  );
}
